// src/components/tariffs/TariffChatbot.tsx
import React, { useState, useEffect, useRef } from "react";
import { apiService } from "../../services/api";
import { Divider } from "@mui/material";
import { Send } from "lucide-react";
import {
  formatDateSeparator,
  parseTimestamp,
  isDifferentDay,
} from "../../utils/timestampUtils";

interface Message {
  id: string;
  text: string;
  sender: "user" | "bot";
  timestamp: Date;
  type: "text" | "calculation" | "suggestion";
  created_at?: string; // Store original timestamp string for date separator logic
}

interface HistoricalMessage {
  id: number;
  session_id: string;
  role: "user" | "assistant";
  content: string;
  created_at: string;
}

interface TariffChatbotProps {
  onSessionCreated?: (sessionId: string) => void;
  onSessionsUpdated?: () => void;
  currentSessionId?: string | null;
  startNewSession?: boolean;
  onNewSessionStarted?: () => void;
  historicalMessages?: HistoricalMessage[];
  activeSessionTitle?: string | null;
}

// ---------- HTS TABLE HELPERS ----------

type HTSMatch = { code: string; description: string; lineIndex: number };

const hasExistingHTSTable = (lines: string[]): boolean => {
  for (let i = 0; i < lines.length - 2; i++) {
    const line = lines[i].trim();
    if (!/^\|.*HTS Code.*\|$/i.test(line)) continue;
    if (!/^\|[\s\-|:]+\|$/.test(lines[i + 1].trim())) continue;
    if (/^\|.*\d{4}\.\d{2}.*\|$/.test(lines[i + 2].trim())) return true;
  }
  return false;
};

const containsDottedHTSCode = (value: string): boolean => {
  let segmentLength = 0;
  let dotCount = 0;

  for (const char of value) {
    const charCode = char.codePointAt(0);
    if (charCode === undefined) continue;
    const isDigit = charCode >= 48 && charCode <= 57;

    if (isDigit) {
      segmentLength++;
      continue;
    }

    if (char === "." && segmentLength > 0) {
      dotCount++;
      segmentLength = 0;
      if (dotCount === 3) return true;
      continue;
    }

    segmentLength = 0;
    dotCount = 0;
  }

  return false;
};

const isHTSSectionEnd = (trimmedLine: string): boolean =>
  trimmedLine === "" ||
  (!containsDottedHTSCode(trimmedLine) &&
    !/^\s*\|/.test(trimmedLine) &&
    !/^[\s\-|:]+$/.test(trimmedLine));

const parseHTSCodeEntries = (lines: string[]): { matches: HTSMatch[]; htsStartIdx: number } => {
  const matches: HTSMatch[] = [];
  let htsStartIdx = -1;
  let foundPrompt = false;
  let foundHeader = false;
  let foundSeparator = false;

  for (let i = 0; i < lines.length; i++) {
    const trimmedLine = lines[i].trim();

    if (/Please select.*HTS|HTS codes?/i.test(trimmedLine)) { foundPrompt = true; continue; }
    if (foundPrompt && !foundHeader && /^HTS Code.*Description$/i.test(trimmedLine)) {
      foundHeader = true; htsStartIdx = i; continue;
    }
    if (foundPrompt && foundHeader && !foundSeparator && /^[\s\-|:]+$/.test(trimmedLine)) {
      foundSeparator = true; continue;
    }

    const htsMatch = /\*\*(\d{4}\.\d{2}(?:\.\d+(?:\.\d+)?)?)\*\*\s*\|\s*(.+)/.exec(trimmedLine);
    if (htsMatch) {
      matches.push({ code: htsMatch[1], description: htsMatch[2].trim(), lineIndex: i });
      continue;
    }

    if (foundPrompt && matches.length > 0 && isHTSSectionEnd(trimmedLine)) break;
  }

  if (htsStartIdx < 0) htsStartIdx = matches[0]?.lineIndex ?? -1;

  return { matches, htsStartIdx };
};

const assembleWithHTSTable = (lines: string[], htsStartIdx: number, htsEndIdx: number, matches: HTSMatch[]): string => {
  const rows = matches.map(({ code, description }) => `| ${code} | ${description} |`).join("\n");
  const table = `| HTS Code | Description |\n| --- | --- |\n${rows}`;
  const parts = [
    lines.slice(0, htsStartIdx).join("\n").trim(),
    table,
    lines.slice(htsEndIdx).join("\n").trim(),
  ].filter(Boolean);
  return parts.join("\n");
};

const TariffChatbot: React.FC<TariffChatbotProps> = ({
  onSessionCreated,
  onSessionsUpdated,
  currentSessionId,
  startNewSession,
  onNewSessionStarted,
  historicalMessages,
  activeSessionTitle,
}) => {
  const [messages, setMessages] = useState<Message[]>([]);

  const [inputText, setInputText] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Sync messages from historical session when a session is selected
  useEffect(() => {
    if (historicalMessages && historicalMessages.length > 0) {
      // Convert historical messages to display format with validated timestamps
      setMessages(
        historicalMessages.map((msg) => {
          const parsedDate = parseTimestamp(msg.created_at);
          return {
            id: msg.id.toString(),
            text: msg.content,
            sender: msg.role === "user" ? "user" : "bot",
            timestamp: parsedDate || new Date(), // Fallback to current date if invalid
            type: "text" as const,
            created_at: msg.created_at, // Store original string for date separator logic
          };
        })
      );
    } else if (!currentSessionId) {
      // No session selected, show empty state
      setMessages([]);
    }
    // Do NOT clear messages when currentSessionId exists but historicalMessages is empty
    // This prevents clearing newly-sent messages that haven't been saved yet
  }, [currentSessionId, historicalMessages]);

  const updateStreamMessage = (botId: string, token: string) => {
    setMessages((prev) =>
      prev.map((m) => (m.id === botId ? { ...m, text: m.text + token } : m)),
    );
  };

  const setStreamError = (botId: string, errMsg: string) => {
    setMessages((prev) =>
      prev.map((m) =>
        m.id === botId
          ? { ...m, text: errMsg || "Sorry, something went wrong." }
          : m,
      ),
    );
  };

  const handleSendMessage = async () => {
    if (!inputText.trim()) return;

    const now = new Date();
    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputText,
      sender: "user",
      timestamp: now,
      type: "text",
      created_at: now.toISOString(), // Store ISO string for date separator logic
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputText("");
    setIsTyping(true);

    try {
      let activeSessionId = currentSessionId || undefined;

      if (!activeSessionId || startNewSession) {
        const sessionRes = await apiService.createChatSession();
        const createdSession = sessionRes.data?.session || {};
        activeSessionId = createdSession.id || createdSession.session_id;
        if (activeSessionId) {
          onSessionCreated?.(activeSessionId);
        }
        if (startNewSession) {
          onNewSessionStarted?.();
        }
      }

      const streamBotId = (Date.now() + 1).toString();
      const botTimestamp = new Date();
      setMessages((prev) => [
        ...prev,
        {
          id: streamBotId,
          text: "",
          sender: "bot",
          timestamp: botTimestamp,
          type: "text",
          created_at: botTimestamp.toISOString(),
        },
      ]);

      let streamError = false;

      await apiService.queryTariffStream(
        userMessage.text,
        activeSessionId,
        false,
        (token) => { updateStreamMessage(streamBotId, token); },
        (meta) => {
          if (meta.session_id && meta.session_id !== currentSessionId) {
            onSessionCreated?.(meta.session_id);
          }
        },
        () => {
          setIsTyping(false);
          onSessionsUpdated?.();
        },
        (errMsg) => {
          streamError = true;
          setStreamError(streamBotId, errMsg);
        },
      );

      if (!streamError) {
        onSessionsUpdated?.();
      }
    } catch (error: any) {
      const errorTimestamp = new Date();
      const botMessage: Message = {
        id: (Date.now() + 1).toString(),
        text:
          error?.response?.data?.error ||
          error?.message ||
          "Sorry, I couldn't fetch a response at the moment.",
        sender: "bot",
        timestamp: errorTimestamp,
        type: "text",
        created_at: errorTimestamp.toISOString(),
      };
      setMessages((prev) => [...prev, botMessage]);
    } finally {
      setIsTyping(false);
    }
  };


  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };


  // ---------- CONVERT HTS CODE LISTS TO TABLE FORMAT ----------
  const convertHTSCodesToTable = (text: string): string => {
    const hasHTSPrompt = /HTS codes?|HTS code|Please select.*HTS|found multiple HTS/i.test(text);
    if (!hasHTSPrompt) return text;

    const lines = text.split("\n");
    if (hasExistingHTSTable(lines)) return text;

    const { matches, htsStartIdx } = parseHTSCodeEntries(lines);
    if (matches.length === 0 || htsStartIdx < 0) return text;

    const htsEndIdx = matches.at(-1)!.lineIndex + 1;
    return assembleWithHTSTable(lines, htsStartIdx, htsEndIdx, matches);
  };

  // ---------- BOT MESSAGE FORMATTER (headings + tables + lists) ----------
  const parseBotMessageLines = (raw: string): string[] => {
    const withHTSTables = convertHTSCodesToTable(raw);
    const noMarkdown = withHTSTables.replace(/\*\*(.*?)\*\*/g, "$1");
    const dedupNumbering = noMarkdown.replace(/(\d+)\.\s+\1\.\s+/g, "$1. ");
    const withNewlines = dedupNumbering.replace(/(\d+)\.\s/g, "\n$1. ");

    return withNewlines
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.length > 0);
  };

  const getStableRenderKeys = (values: string[], prefix: string): string[] => {
    const occurrences = new Map<string, number>();
    return values.map((value = "empty") => {
      const nextCount = (occurrences.get(value) ?? 0) + 1;
      occurrences.set(value, nextCount);
      return `${prefix}-${value}-${nextCount}`;
    });
  };

  const flushListItems = (
    elements: React.ReactNode[],
    listItems: string[],
  ): void => {
    if (!listItems.length) return;

    const itemKeys = getStableRenderKeys(listItems, "list-item");
    elements.push(
      <ol
        key={`list-${elements.length}`}
        style={{ paddingLeft: 20, marginBottom: 8 }}
      >
        {listItems.map((item, itemIndex) => (
          <li key={itemKeys[itemIndex]}>
            <p className="text-sm leading-relaxed">{item}</p>
          </li>
        ))}
      </ol>
    );

    listItems.length = 0;
  };

  const parseTableRow = (line: string) =>
    line
      .slice(1, -1)
      .split("|")
      .map((cell) => cell.trim());

  const isTableLine = (line: string) => /^\|(.+)\|$/.test(line);
  const isSeparatorLine = (line: string) => /^\|[-:\s|]+\|$/.test(line);

  const getHeadingMatch = (line: string) => {
    const headingPatterns = [
      { pattern: /^#\s+(.*)/, sizeClass: "text-base mt-4 mb-2" },
      { pattern: /^##\s+(.*)/, sizeClass: "text-sm mt-3 mb-1.5" },
      { pattern: /^###\s+(.*)/, sizeClass: "text-xs mt-2 mb-1" },
    ];

    for (const heading of headingPatterns) {
      const match = heading.pattern.exec(line);
      if (match) {
        return { text: match[1], sizeClass: heading.sizeClass };
      }
    }

    return null;
  };

  const pushHeading = (elements: React.ReactNode[], line: string): boolean => {
    const heading = getHeadingMatch(line);
    if (!heading) return false;

    elements.push(
      <p
        key={`h-${elements.length}`}
        className={[
          "font-bold",
          heading.sizeClass,
          elements.length === 0 ? "mt-0" : "",
        ].join(" ")}
      >
        {heading.text}
      </p>
    );

    return true;
  };

  const parseTableBlock = (lines: string[], startIndex: number) => {
    if (!isTableLine(lines[startIndex])) return null;

    const headers = parseTableRow(lines[startIndex]).filter(Boolean);
    const rows: string[][] = [];
    let nextIndex = startIndex + 1;

    if (nextIndex < lines.length && isSeparatorLine(lines[nextIndex])) {
      nextIndex++;
    }

    while (
      nextIndex < lines.length &&
      isTableLine(lines[nextIndex]) &&
      !isSeparatorLine(lines[nextIndex])
    ) {
      rows.push(parseTableRow(lines[nextIndex]));
      nextIndex++;
    }

    return { headers, rows, nextIndex };
  };

  const pushTable = (
    elements: React.ReactNode[],
    headers: string[],
    rows: string[][],
  ) => {
    const headerKeys = getStableRenderKeys(headers, "table-header");
    const rowKeys = getStableRenderKeys(
      rows.map((row) => row.join("|")),
      "table-row"
    );

    elements.push(
      <div
        key={`table-${elements.length}`}
        style={{ marginBottom: 12, marginTop: 4, width: "100%", overflowX: "auto" }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "0.8125rem",
          }}
        >
          <thead>
            <tr>
              {headers.map((header, headerIndex) => (
                <th key={headerKeys[headerIndex]} style={{ border: "1px solid #EAEAEA", background: "#F7F7F7", padding: "6px 10px", textAlign: "left", fontWeight: 700, whiteSpace: "nowrap" }}>{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => {
              const cellKeys = getStableRenderKeys(row, `${rowKeys[rowIndex]}-cell`);
              return (
                <tr key={rowKeys[rowIndex]}>
                  {row.map((cell, cellIndex) => (
                    <td key={cellKeys[cellIndex]} style={{ border: "1px solid #EAEAEA", padding: "6px 10px", verticalAlign: "top", lineHeight: 1.5 }}>{cell}</td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const getOrderedListItem = (line: string): string | null => {
    const match = /^(\d+)\.\s+(.*)/.exec(line);
    return match ? match[2] : null;
  };

  const pushParagraph = (elements: React.ReactNode[], line: string) => {
    elements.push(
      <p
        key={`p-${elements.length}`}
        className="mb-2 text-sm leading-relaxed"
      >
        {line}
      </p>
    );
  };

  const renderBotMessage = (raw: string) => {
    const lines = parseBotMessageLines(raw);
    const elements: React.ReactNode[] = [];
    let listItems: string[] = [];

    let i = 0;
    while (i < lines.length) {
      const line = lines[i];
      const listItem = getOrderedListItem(line);

      if (listItem) {
        listItems.push(listItem);
        i++;
        continue;
      }

      flushListItems(elements, listItems);
      if (pushHeading(elements, line)) {
        i++;
        continue;
      }

      const tableBlock = parseTableBlock(lines, i);
      if (tableBlock) {
        pushTable(elements, tableBlock.headers, tableBlock.rows);
        i = tableBlock.nextIndex;
        continue;
      }

      pushParagraph(elements, line);
      i++;
    }

    flushListItems(elements, listItems);
    return elements;
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, height: "100%" }}>
      {/* CHAT MESSAGES */}
      <div
        style={{ flex: 1, overflowY: "auto", padding: "24px 16px" }}
      >
        <div className="w-full p-0">
          {messages.length === 0 && !isTyping && (
            <div style={{ display: "flex", justifyContent: "flex-start", padding: "4px 0" }}>
              <div style={{ maxWidth: "80%", borderRadius: 12, border: "1px solid #EAEAEA", background: "#F7F7F7", padding: "12px 16px", color: "#111111" }}>
                <p style={{ margin: 0, fontSize: "0.875rem", lineHeight: 1.6 }}>Hello! I'm your WorldLink Decision Intelligence Assistant. How can I help you analyze your supply chain today?</p>
              </div>
            </div>
          )}
          {messages.map((message, index) => {
            const prevMessage = index > 0 ? messages[index - 1] : null;
            const showDateSeparator =
              prevMessage &&
              message.created_at &&
              prevMessage.created_at &&
              isDifferentDay(message.created_at, prevMessage.created_at);

            return (
              <React.Fragment key={message.id}>
                {showDateSeparator && (
                  <div className="flex justify-center px-0 py-3">
                    <Divider sx={{ width: "100%" }}>
                      <span className="font-['Inter'] px-2 text-[0.75rem] font-semibold text-[#9CA3AF]">
                        {formatDateSeparator(message.created_at)}
                      </span>
                    </Divider>
                  </div>
                )}
                {index === 0 && !showDateSeparator && (
                  <div className="flex justify-center px-0 py-3">
                    <Divider sx={{ width: "100%" }}>
                      <span className="font-['Inter'] px-2 text-[0.75rem] font-semibold text-[#9CA3AF]">
                        {formatDateSeparator(message.created_at)}
                      </span>
                    </Divider>
                  </div>
                )}
                <div
                  style={{ display: "flex", padding: "4px 0", justifyContent: message.sender === "user" ? "flex-end" : "flex-start" }}
                >
                  <div
                    style={{
                      maxWidth: "80%",
                      borderRadius: 12,
                      padding: "10px 14px",
                      background: message.sender === "user" ? "#EF2D3E" : "#F7F7F7",
                      color: message.sender === "user" ? "#fff" : "#111111",
                      border: message.sender === "user" ? "none" : "1px solid #EAEAEA",
                    }}
                  >
                    {message.sender === "bot" ? (
                      <div style={{ fontSize: "0.875rem", lineHeight: 1.6 }}>{renderBotMessage(message.text)}</div>
                    ) : (
                      <p style={{ margin: 0, whiteSpace: "pre-wrap", fontSize: "0.875rem", lineHeight: 1.5 }}>
                        {message.text}
                      </p>
                    )}
                  </div>
                </div>
              </React.Fragment>
            );
          })}

          {isTyping && (
            <div style={{ display: "flex", justifyContent: "flex-start", padding: "4px 0" }}>
              <div style={{ borderRadius: 12, border: "1px solid #EAEAEA", background: "#F7F7F7", padding: "10px 14px", display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ width: 16, height: 16, borderRadius: "50%", border: "2px solid #EF2D3E", borderTopColor: "transparent", animation: "spin 0.7s linear infinite" }} />
                <p style={{ margin: 0, fontSize: "0.875rem", color: "#6B7280" }}>Typing...</p>
              </div>
            </div>
          )}
        </div>
        <div ref={messagesEndRef} />
      </div>

      {/* INPUT AREA */}
      <div style={{ borderTop: "1px solid #EAEAEA", background: "#fff", padding: 16 }}>
        <div style={{ display: "flex", gap: 8 }}>
          <input
            style={{ flex: 1, borderRadius: 8, border: "1px solid #EAEAEA", padding: "8px 16px", fontSize: "0.875rem", color: "#111111", outline: "none", fontFamily: "'Inter', sans-serif" }}
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={(e) => { e.currentTarget.style.border = "1px solid #EF2D3E"; e.currentTarget.style.boxShadow = "0 0 0 2px rgba(239,45,62,0.15)"; }}
            onBlur={(e) => { e.currentTarget.style.border = "1px solid #EAEAEA"; e.currentTarget.style.boxShadow = "none"; }}
            disabled={isTyping}
            placeholder="Ask a question..."
          />
          <button
            onClick={handleSendMessage}
            disabled={!inputText.trim() || isTyping}
            style={{ background: "#EF2D3E", border: "none", borderRadius: 8, padding: 8, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", opacity: (!inputText.trim() || isTyping) ? 0.4 : 1 }}
            onMouseEnter={(e) => { if (inputText.trim() && !isTyping) (e.currentTarget as HTMLButtonElement).style.background = "#d42635"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "#EF2D3E"; }}
          >
            <Send size={20} color="#fff" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default TariffChatbot;
